/**
 * The publishing plan: what we put out on our own channels.
 *
 * Not user-facing content — this is Skida's own Instagram, Facebook, TikTok,
 * YouTube, website and LinkedIn.
 *
 * ## Channel order is load-bearing
 *
 * `CHANNELS` is a fixed order, and the table renders a slot for every one of
 * them on every row whether the post uses it or not. That is the whole point of
 * the design's dot column: Facebook's dot always sits in the same horizontal
 * position, so "which posts touch channel X" is answered by reading down a
 * column rather than reading the text of every row.
 *
 * Sorting or filtering this array would destroy that property silently.
 */

export const CHANNELS = [
  'Instagram',
  'Facebook',
  'TikTok',
  'YouTube',
  'Website',
  'LinkedIn',
  // Appended, never inserted. The dot column identifies a channel by its
  // horizontal position, so putting a new one anywhere but the end would move
  // every dot to its right and silently change what old rows appear to say.
  'Threads',
] as const

export type Channel = (typeof CHANNELS)[number]

/** Each platform's own brand colour, so a dot is recognisable without a label. */
export const CHANNEL_COLORS: Record<Channel, string> = {
  Instagram: '#c13584',
  Facebook: '#1877f2',
  TikTok: '#000000',
  YouTube: '#ff0000',
  Website: '#3456DA',
  LinkedIn: '#0a66c2',
  // Threads' mark is black, and so is TikTok's. Tolerable here and only here:
  // the column's whole design is that position identifies the channel, with
  // colour as recognition rather than as the distinguishing signal. Both dots
  // also carry a title.
  Threads: '#000000',
}

/**
 * Where each channel actually lives, from the design.
 *
 * Kept as a constant rather than waiting for the collector, so an unconnected
 * card can still take you to the account. Two of these were news: TikTok and
 * LinkedIn exist and are ours, which was not obvious from anywhere else.
 */
export const CHANNEL_URLS: Partial<Record<Channel, string>> = {
  Instagram: 'https://www.instagram.com/skida.app/',
  Facebook: 'https://www.facebook.com/skidaapp',
  TikTok: 'https://www.tiktok.com/@skida.app',
  YouTube: 'https://www.youtube.com/@skida_app',
  LinkedIn: 'https://www.linkedin.com/company/skida-alpine-adventures',
  Threads: 'https://www.threads.net/@skida.app',
}

/**
 * The sites the Website card stands for.
 *
 * Three, per the design. Only the first has a GA4 property we can read, so the
 * figure on that card is skida.app alone — the card lists all three so nobody
 * reads one number as covering them.
 */
export const SKIDA_WEBSITES = ['https://skida.app', 'https://mountainsnow.ski', 'https://skida.pro']

export const CONTENT_TYPES = ['Post', 'Reel/Video', 'Story', 'Article', 'Newsletter teaser'] as const
export type ContentType = (typeof CONTENT_TYPES)[number]

/**
 * Idea comes first — decision 28.
 *
 * Somewhere to park something worth doing before it has a date or a channel.
 * Without it the first step was Draft, which implies copy exists, so half-formed
 * ideas either got a fake date or never got written down at all.
 */
export type PostStatus = 'idea' | 'draft' | 'planned' | 'published'

export const POST_STATUSES: { key: PostStatus; label: string; color: string; bg: string }[] = [
  { key: 'idea', label: 'Idea', color: '#8f96a3', bg: '#f1f2f3' },
  { key: 'draft', label: 'Draft', color: '#4c5b82', bg: '#f1f2f3' },
  { key: 'planned', label: 'Planned', color: '#3456DA', bg: '#eaefff' },
  { key: 'published', label: 'Published', color: '#3aa52f', bg: '#eaf7e7' },
]

export const postStatusStyle = (s: PostStatus) =>
  POST_STATUSES.find((x) => x.key === s) ?? POST_STATUSES[0]!

/**
 * One reusable asset attached to a post.
 *
 * The design calls this the materials archive, and gives the reason: "so copy
 * and creative can be found and reused later rather than re-written per post."
 *
 * Images are held as a URL rather than an upload. Firebase Storage is not set up
 * in this project, and a drag-drop slot that silently loses the file on reload
 * would be worse than a field that admits what it is.
 */
export interface PostAsset {
  id: string
  kind: 'text' | 'image' | 'video'
  title: string
  /** For `text`. The caption or copy block. */
  text: string
  /** For `image` and `video`. */
  url: string
}

/** Collection: `skidaCrmPosts`. */
export interface Post {
  id: string
  /** ISO date, plus a separate `HH:MM` — the design keeps them apart. */
  date: string
  time: string
  /** A post can target several channels at once. Never a single string. */
  channels: Channel[]
  type: ContentType
  status: PostStatus
  owner: string
  /** URL of the actual published post, typed in by hand. */
  link: string
  assets: PostAsset[]

  /**
   * ---- What the post is for ----
   *
   * From the 15 Aug round. An idea with no date and no channel still has a
   * subject and a reason to exist, and those are what make it reviewable
   * before anybody writes copy.
   */
  subject: string
  goal: string
  targetAudience: string
  /**
   * Which product it serves, as two independent flags rather than one choice —
   * decision 21. A post can serve both, and a new one starts with neither
   * rather than defaulting to a product nobody picked.
   */
  forPro: boolean
  forApp: boolean

  /**
   * ---- Links ----
   *
   * One event, any number of ambassadors. Stored as document ids, and the
   * reverse direction (an event's posts, an ambassador's posts) is derived by
   * querying these rather than by keeping a second copy on the other record.
   */
  linkedEventId: string
  linkedAmbassadorIds: string[]
  /**
   * Partners this post was run for or with.
   *
   * The design lists it beside the ambassadors and it is the link that lets the
   * publishing plan answer "what did we run for Fri Flyt this season" — which
   * is the question a partner asks at renewal. Several, because a post can
   * serve a partner and an event at once.
   */
  linkedPartnerIds: string[]

  notes: string
  createdAt: string | null
  updatedAt: string | null
}

/**
 * Who a new post belongs to until somebody says otherwise.
 *
 * Marketing owns what goes out on our own channels, so the plan is Lara's — a
 * new post lands there rather than on whoever happened to open the form, which
 * is what it did while the owner was a free-text field prefilled with your own
 * name.
 *
 * By address, because that is the identity that survives a rename; the field
 * stores the display name, which is what the table, the header filter and the
 * Mine chip compare. Nobody at all is the fallback when that address has no CRM
 * record: the plan is still not the opener's.
 */
export const DEFAULT_POST_OWNER_EMAIL = 'lara@mountainsnow.ski'

export const EMPTY_POST: Omit<Post, 'id'> = {
  date: '',
  time: '',
  channels: [],
  type: 'Post',
  // A new post is an idea until somebody decides otherwise.
  status: 'idea',
  owner: '',
  link: '',
  assets: [],
  linkedEventId: '',
  linkedAmbassadorIds: [],
  linkedPartnerIds: [],
  subject: '',
  goal: '',
  targetAudience: '',
  forPro: false,
  forApp: false,
  notes: '',
  createdAt: null,
  updatedAt: null,
}

/**
 * When a post goes out, as one label.
 *
 * Undated is normal now that Idea is the first status, so this returns a single
 * string rather than a date and a time to be joined around a literal separator —
 * which rendered as a bare "·" on every ideas row.
 */
export function whenLabel(p: Pick<Post, 'date' | 'time'>): string {
  if (!p.date) return 'No date yet'
  const [y, m, d] = p.date.split('-')
  const date = y && m && d ? `${d}.${m}.${y}` : p.date
  return p.time ? `${date} · ${p.time}` : date
}
